import type { RefObject } from "react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Slider } from "../ui/slider";
import { Toggle } from "../ui/toggle";
import type { Chapter } from "../../types";
import {
  BOOK_CONTENT_KIND_LABELS,
  BOOK_CONTENT_KINDS,
  type BookContentKind,
} from "../../../shared/bookSpine";
import { ChevronLeftIcon, ChevronRightIcon } from "./icons";
import {
  storyCraftScoreBorderColor,
  type ScoreBlendMode,
  type ScoreBlendSettings,
} from "../../../shared/scoreBlend";

export type ChapterStatus = "draft" | "in_review" | "final";

const MATTER_KINDS = BOOK_CONTENT_KINDS.filter((k) => k !== "chapter");
const SCORE_BORDER_WIDTH_PX = 6;

function spineTitle(chapter: Chapter, bodyLabel: string, numberBodyItems: boolean): string {
  const kind = (chapter.kind ?? "chapter") as BookContentKind;
  if (kind === "chapter") {
    if (!numberBodyItems) return chapter.title;
    const n = chapter.chapterNumber ?? chapter.order;
    return `${n}. ${chapter.title}`;
  }
  return chapter.title;
}

function kindLabel(chapter: Chapter, bodyLabel: string, numberBodyItems: boolean): string {
  const kind = (chapter.kind ?? "chapter") as BookContentKind;
  if (kind === "chapter") {
    if (!numberBodyItems) return bodyLabel;
    const n = chapter.chapterNumber ?? chapter.order;
    return `${bodyLabel.slice(0, 2)} ${n}`;
  }
  return BOOK_CONTENT_KIND_LABELS[kind] ?? kind;
}

function scoreBorderColor(score: number | null | undefined, kind: BookContentKind): string {
  if (kind !== "chapter") return "var(--border)";
  if (score == null || !Number.isFinite(score)) {
    return "color-mix(in oklab, var(--muted-foreground) 35%, transparent)";
  }
  return storyCraftScoreBorderColor(score);
}

function chapterStatusBadgeClass(status: ChapterStatus): string {
  switch (status) {
    case "in_review":
      return "border-primary/40 bg-primary/10 text-primary";
    case "final":
      return "border-foreground/20 bg-foreground/10 text-foreground";
    default:
      return "border-border bg-muted text-muted-foreground";
  }
}

export function SidebarChaptersPanel({
  chapterSearchRef,
  chapterSearch,
  onChapterSearchChange,
  statusFilter,
  onStatusFilterChange,
  tagFilter,
  onTagFilterChange,
  allTags,
  visibleChapters,
  activeChapterId,
  onSelectChapter,
  onCollapse,
  onAddChapter,
  onInsertRelative,
  onChangeKind,
  onMoveChapter,
  onMoveRelative,
  onDeleteChapter,
  setDragChapterId,
  onDropReorder,
  countWords,
  statusLabel,
  storyCraftAverages,
  showSpineMatterInsert = true,
  showScoreBlendControls = false,
  scoreBlend,
  onScoreBlendChange,
  bodyLabel = "Chapter",
  bodyLabelPlural = "Chapters",
  numberBodyItems = true,
  allowMultipleBodyItems = true,
}: {
  chapterSearchRef: RefObject<HTMLInputElement | null>;
  chapterSearch: string;
  onChapterSearchChange: (value: string) => void;
  statusFilter: "all" | ChapterStatus;
  onStatusFilterChange: (value: "all" | ChapterStatus) => void;
  tagFilter: string;
  onTagFilterChange: (value: string) => void;
  allTags: string[];
  visibleChapters: Chapter[];
  activeChapterId: string;
  onSelectChapter: (id: string) => void;
  onCollapse: () => void;
  onAddChapter: () => void;
  onInsertRelative: (relativeId: string, position: "before" | "after", kind: BookContentKind) => void;
  onChangeKind?: (chapterId: string, kind: BookContentKind) => void;
  onMoveChapter: (chapterId: string) => void;
  onMoveRelative: (chapterId: string, direction: -1 | 1) => void;
  onDeleteChapter: (chapterId: string) => void;
  setDragChapterId: (id: string | null) => void;
  onDropReorder: (targetId: string) => void;
  countWords: (text: string) => number;
  statusLabel: (status: ChapterStatus) => string;
  storyCraftAverages: Record<string, number | null>;
  /** Novice hides front/back matter insert until they switch persona. */
  showSpineMatterInsert?: boolean;
  showScoreBlendControls?: boolean;
  scoreBlend?: ScoreBlendSettings;
  onScoreBlendChange?: (next: ScoreBlendSettings) => void;
  bodyLabel?: string;
  bodyLabelPlural?: string;
  numberBodyItems?: boolean;
  allowMultipleBodyItems?: boolean;
}) {
  const blendModes: Array<{ mode: ScoreBlendMode; label: string }> = [
    { mode: "storycraft", label: "AI" },
    { mode: "editor", label: "Editor" },
    { mode: "weighted", label: "Blend" },
  ];
  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden border-r border-border bg-(--workbench-sideBar)" role="region" aria-label={`${bodyLabelPlural} panel`}>
      <Card className="m-1.5 min-h-0 flex-1 gap-0 rounded-lg border border-border bg-card py-0 shadow-sm ring-0">
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border bg-muted/20 px-2 py-1.5">
          <span className="text-[0.72rem] font-bold uppercase tracking-[0.07em] text-muted-foreground">{bodyLabelPlural}</span>
          <Button size="icon-sm" variant="ghost" className="h-[1.85rem] w-[1.85rem] min-w-[1.85rem] p-0 text-muted-foreground hover:text-foreground [&_svg]:size-4" onClick={onCollapse} title="Collapse chapters panel" aria-label="Collapse chapters panel">
            <ChevronLeftIcon />
          </Button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden p-2">
          <Input
            ref={chapterSearchRef}
            aria-label={`Search ${bodyLabelPlural.toLowerCase()}`}
            placeholder="Search title or content"
            value={chapterSearch}
            onChange={(event) => onChapterSearchChange(event.target.value)}
          />
          <div className="grid grid-cols-2 gap-1.5">
            <Select
              value={statusFilter}
              onValueChange={(value) => onStatusFilterChange(value as "all" | ChapterStatus)}
            >
              <SelectTrigger size="default" aria-label="Filter chapters by status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="in_review">In review</SelectItem>
                <SelectItem value="final">Final</SelectItem>
              </SelectContent>
            </Select>
            <Select value={tagFilter} onValueChange={(value) => onTagFilterChange(value)}>
              <SelectTrigger size="default" aria-label="Filter chapters by tag">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tags</SelectItem>
                {allTags.map((tag) => (
                  <SelectItem key={tag} value={tag}>
                    {tag}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {showScoreBlendControls && scoreBlend && onScoreBlendChange ? (
            <div className="flex flex-col gap-1.5 rounded-lg border border-border bg-card p-1.5" aria-label="Chapter score border source">
              <div className="grid grid-cols-3 gap-1">
                {blendModes.map(({ mode, label }) => (
                  <Toggle
                    key={mode}
                    variant="outline"
                    size="sm"
                    pressed={scoreBlend.mode === mode}
                    className="h-7 min-w-0 px-1 text-[0.68rem] text-muted-foreground data-[state=on]:border-primary/40"
                    onClick={() => onScoreBlendChange({ ...scoreBlend, mode })}
                  >
                    {label}
                  </Toggle>
                ))}
              </div>
              {scoreBlend.mode === "weighted" ? (
                <label className="flex flex-col gap-1">
                  <span className="flex justify-between text-[0.62rem] text-muted-foreground">
                    <span>Editor</span>
                    <span>AI</span>
                  </span>
                  <Slider
                    min={0}
                    max={100}
                    step={5}
                    aria-label="StoryCraft weight"
                    value={[Math.round(scoreBlend.weightStoryCraft * 100)]}
                    onValueChange={([value]) =>
                      onScoreBlendChange({
                        mode: "weighted",
                        weightStoryCraft: value / 100,
                      })
                    }
                  />
                </label>
              ) : null}
            </div>
          ) : null}
          {allowMultipleBodyItems ? (
            <Button size="sm" variant="secondary" className="w-full shrink-0" onClick={onAddChapter}>
              Add {bodyLabel.toLowerCase()}
            </Button>
          ) : null}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain rounded-lg border border-border p-1.5 [-webkit-overflow-scrolling:touch]">
            <div role="listbox" aria-label="Chapter list" className="flex min-h-min flex-col">
              {visibleChapters.map((chapter, index) => {
                const kind = (chapter.kind ?? "chapter") as BookContentKind;
                const avg = storyCraftAverages[chapter.id] ?? null;
                const isFirst = index === 0;
                const isLast = index === visibleChapters.length - 1;
                const isSelected = chapter.id === activeChapterId;
                const status = chapter.metadata.status as ChapterStatus;
                return (
                  <div
                    key={chapter.id}
                    className={`group flex w-full items-center gap-1 rounded-lg border py-0.5 pr-1 pl-0.5 text-foreground transition-colors ${
                      isSelected
                        ? "border-primary/40 bg-primary/10 ring-1 ring-primary/20"
                        : "border-transparent bg-transparent hover:bg-muted/70"
                    }`}
                    style={{ borderLeft: `${SCORE_BORDER_WIDTH_PX}px solid ${scoreBorderColor(avg, kind)}` }}
                  >
                    <Button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      draggable
                      onDragStart={() => setDragChapterId(chapter.id)}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => onDropReorder(chapter.id)}
                      onClick={() => onSelectChapter(chapter.id)}
                      variant="ghost"
                      className="h-auto min-w-0 flex-1 justify-between gap-2.5 px-1.5 py-1.5 text-left whitespace-normal hover:bg-transparent"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-foreground" title={spineTitle(chapter, bodyLabel, numberBodyItems)}>
                          {spineTitle(chapter, bodyLabel, numberBodyItems)}
                        </div>
                        <div className="mt-0.5 truncate text-[0.72rem] font-normal text-muted-foreground">
                          {kindLabel(chapter, bodyLabel, numberBodyItems)}
                          {kind === "chapter" ? ` · ${countWords(chapter.content)} words` : ""}
                          {kind === "chapter" && avg != null ? ` · ${avg.toFixed(1)}` : ""}
                          {chapter.metadata.tags[0] ? ` · ${chapter.metadata.tags[0]}` : ""}
                        </div>
                      </div>
                      <Badge variant="outline" className={chapterStatusBadgeClass(status)}>
                        {statusLabel(status)}
                      </Badge>
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          className="size-7 shrink-0 text-base leading-none text-muted-foreground opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 data-[state=open]:opacity-100"
                          title="Chapter options"
                          aria-label={`Options for ${spineTitle(chapter, bodyLabel, numberBodyItems)}`}
                          onClick={(event) => event.stopPropagation()}
                        >
                          ⋮
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem onSelect={() => onInsertRelative(chapter.id, "before", "chapter")}>
                          Insert chapter before
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => onInsertRelative(chapter.id, "after", "chapter")}>
                          Insert chapter after
                        </DropdownMenuItem>
                        {showSpineMatterInsert ? (
                          <>
                            {(["before", "after"] as const).map((position) => (
                              <DropdownMenuSub key={position}>
                                <DropdownMenuSubTrigger>Insert other {position}…</DropdownMenuSubTrigger>
                                <DropdownMenuSubContent className="w-52">
                                  <DropdownMenuLabel className="whitespace-normal">
                                    Usually added when you&apos;re ready to publish
                                  </DropdownMenuLabel>
                                  {MATTER_KINDS.map((k) => (
                                    <DropdownMenuItem
                                      key={`${position}-${k}`}
                                      onSelect={() => onInsertRelative(chapter.id, position, k)}
                                    >
                                      {BOOK_CONTENT_KIND_LABELS[k]}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                            ))}
                            {onChangeKind ? (
                              <DropdownMenuSub>
                                <DropdownMenuSubTrigger>Change type…</DropdownMenuSubTrigger>
                                <DropdownMenuSubContent className="w-44">
                                  {BOOK_CONTENT_KINDS.map((k) => (
                                    <DropdownMenuItem
                                      key={`change-${k}`}
                                      disabled={k === kind}
                                      onSelect={() => onChangeKind(chapter.id, k)}
                                    >
                                      {BOOK_CONTENT_KIND_LABELS[k]}
                                      {k === kind ? " (current)" : ""}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                            ) : null}
                          </>
                        ) : null}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          disabled={isFirst}
                          onSelect={() => onMoveRelative(chapter.id, -1)}
                        >
                          Move up
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={isLast}
                          onSelect={() => onMoveRelative(chapter.id, 1)}
                        >
                          Move down
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => onMoveChapter(chapter.id)}>
                          Move before/after…
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          variant="destructive"
                          disabled={visibleChapters.length <= 1}
                          onSelect={() => {
                            if (window.confirm(`Delete “${chapter.title}”? This cannot be undone.`)) {
                              onDeleteChapter(chapter.id);
                            }
                          }}
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

export function SidebarChaptersRail({ onExpand }: { onExpand: () => void }) {
  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden border-r border-border bg-(--workbench-sideBar)" role="region" aria-label="Chapters panel collapsed">
      <Button type="button" variant="ghost" className="h-full min-h-0 w-full flex-col gap-2 rounded-lg border border-border bg-card/70 px-0 py-2.5 text-muted-foreground hover:border-primary/40 hover:bg-primary/10 hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2" onClick={onExpand} title="Show chapters panel" aria-label="Show chapters panel">
        <ChevronRightIcon />
        <span className="rotate-180 [writing-mode:vertical-rl] text-[0.72rem] font-semibold uppercase tracking-[0.08em]">Chapters</span>
      </Button>
    </div>
  );
}
